import { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { getController } from '../controller'
import behaviorTracker from '../behavior-tracker'
import actionRegistry from '../action-registry'
import LocalUserPreferences from 'aldoo-ra/LocalUserPreferences'
import { getValue } from 'aldoo-ra/Value'
import { api } from 'aldoo-ra/API'
import { url } from 'aldoo-ra/CMS/url'
import { parseBubbleContent } from './parse-bubble-content'
import SpeechBubble from './SpeechBubble'
import ProductBubble from './ProductBubble'
import ActionList from './ActionList'
import InputBar from './InputBar'

const agentAPI = api('agent')

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"

const POSITIONS = {
  'bottom-left':  { bottom: 24, left: 24 },
  'bottom-right': { bottom: 24, right: 24 },
}

export default function AgentWidget({
  agentId,
  triggerKey,
  avatarUrl: avatarUrlProp = null,
  agentName: agentNameProp = 'Agent',
  position = 'bottom-right',
}) {
  const [allowed, setAllowed] = useState(null) // null = loading
  const [agentName, setAgentName] = useState(agentNameProp)
  const [avatarUrl, setAvatarUrl] = useState(avatarUrlProp)
  const [pathname, setPathname] = useState(typeof window !== 'undefined' ? window.location.pathname : '/')
  const locationsRef = useRef(null) // cache fetched locations so URL changes don't re-fetch
  const [streamedText, setStreamedText] = useState('')
  const [bubbleVisible, setBubbleVisible] = useState(false)
  const [streaming, setStreaming] = useState(false)
  const [waiting, setWaiting] = useState(false)
  const [actions, setActions] = useState([])
  const [actionsVisible, setActionsVisible] = useState(false)
  const [inputDisabled, setInputDisabled] = useState(false)
  const [inputOpen, setInputOpen] = useState(false)
  const [inputClosing, setInputClosing] = useState(false)
  const [inputPlaceholder, setInputPlaceholder] = useState('')

  const chunkRef = useRef('')
  const rafRef = useRef(null)
  const controllerRef = useRef(null)
  const greetingIntervalMsRef = useRef(24 * 60 * 60 * 1000)
  // Track current state in refs so onChunk closures can read current values
  const streamingRef = useRef(false)
  const waitingRef = useRef(false)
  const bubbleVisibleRef = useRef(false)
  const waitingTimeoutRef = useRef(null)

  const flushChunks = useCallback(() => {
    setStreamedText(chunkRef.current)
    rafRef.current = null
  }, [])

  const checkLocation = useCallback((currentPathname, locations, adminAccess) => {
    const isAdminPath = /\/admin(\/|$)/.test(currentPathname)
    if (isAdminPath && !adminAccess) return false
    // Strip optional language prefix (e.g. /bg/pricing → /pricing, /bg → /)
    const cleanPath = currentPathname.replace(/^\/[a-z]{2}(\/|$)/, (_, sep) => sep || '/')
    if (!locations || locations.length === 0) return true
    return locations.some(pattern =>
      pattern.endsWith('*')
        ? cleanPath.startsWith(pattern.slice(0, -1))
        : cleanPath === pattern
    )
  }, [])

  // Fetch config once, cache locations; re-evaluate on URL changes
  useEffect(() => {
    agentAPI({ config: { agentId } }).then(res => {
      if (res?.error) { setAllowed(false); return }
      if (res?.name) setAgentName(res.name)
      if (res?.avatarUrl) setAvatarUrl(res.avatarUrl)
      if (res?.greetingIntervalMs) greetingIntervalMsRef.current = res.greetingIntervalMs
      locationsRef.current = { locations: res?.locations || [], adminAccess: !!res?.adminAccess }
      setAllowed(checkLocation(window.location.pathname, res?.locations || [], !!res?.adminAccess))
    }).catch(() => setAllowed(false))
  }, [agentId, checkLocation])

  // Re-evaluate on client-side navigation (popstate = back/forward, aa-navigate = pushState)
  useEffect(() => {
    const handleNav = () => setPathname(window.location.pathname)

    // Patch pushState once to emit a custom event so we catch Vike's navigate()
    if (!window.__aaNavPatched) {
      const orig = history.pushState.bind(history)
      history.pushState = (...args) => {
        orig(...args)
        window.dispatchEvent(new Event('aa-navigate'))
      }
      window.__aaNavPatched = true
    }

    window.addEventListener('popstate', handleNav)
    window.addEventListener('aa-navigate', handleNav)
    return () => {
      window.removeEventListener('popstate', handleNav)
      window.removeEventListener('aa-navigate', handleNav)
    }
  }, [])

  useEffect(() => {
    if (!locationsRef.current) return
    const { locations, adminAccess } = locationsRef.current
    setAllowed(checkLocation(pathname, locations, adminAccess))
  }, [pathname, checkLocation])

  useEffect(() => {
    if (allowed !== true) return
    const ctrl = getController(agentId)
    controllerRef.current = ctrl

    function onChunk({ text }) {
      chunkRef.current += text
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(flushChunks)
      }
      // Only call setState when value actually needs to change
      if (!streamingRef.current) { streamingRef.current = true; setStreaming(true) }
      if (waitingRef.current) { waitingRef.current = false; setWaiting(false) }
      if (!bubbleVisibleRef.current) { bubbleVisibleRef.current = true; setBubbleVisible(true) }
    }

    function clearWaitingTimeout() {
      if (waitingTimeoutRef.current) {
        clearTimeout(waitingTimeoutRef.current)
        waitingTimeoutRef.current = null
      }
    }

    function onReceived() {
      // Backend confirmed it received the message — clear the send timeout
      clearWaitingTimeout()
    }

    function onDone() {
      clearWaitingTimeout()
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
      setStreamedText(chunkRef.current)
      streamingRef.current = false; setStreaming(false)
      waitingRef.current = false; setWaiting(false)
      setInputDisabled(false)
    }

    function onFunction({ name, params } = {}) {
      if (name === 'suggest_actions' && Array.isArray(params?.actions)) {
        setActions(params.actions)
        setActionsVisible(true)
      }
      if (name === 'suggest_placeholder' && params?.text) {
        setInputPlaceholder(params.text)
      }
      // All other action execution is handled by the controller
    }

    function onError({ error, detail } = {}) {
      clearWaitingTimeout()
      console.warn('[AldooAgent] error:', error, detail)
      const msg = "I'm sorry, there was a technical difficulty. Please try again."
      chunkRef.current = msg
      setStreamedText(msg)
      streamingRef.current = false; setStreaming(false)
      waitingRef.current = false; setWaiting(false)
      if (!bubbleVisibleRef.current) { bubbleVisibleRef.current = true; setBubbleVisible(true) }
      setInputDisabled(false)
      setTimeout(() => {
        bubbleVisibleRef.current = false
        setBubbleVisible(false)
        chunkRef.current = ''
        setStreamedText('')
      }, 4000)
    }

    function onResumed({ messages }) {
      // Don't restore the greeting bubble if we've already greeted within the interval
      const lastGreet = LocalUserPreferences.get('agent-data.last_greet')
      if (lastGreet && Date.now() - lastGreet < greetingIntervalMsRef.current) return
      // Restore the last agent message so the bubble reappears
      const lastAgent = [...messages].reverse().find(m => m.role === 'agent')
      if (!lastAgent?.content) return
      chunkRef.current = lastAgent.content
      setStreamedText(lastAgent.content)
      bubbleVisibleRef.current = true
      setBubbleVisible(true)
    }

    ctrl.on('chunk', onChunk)
    ctrl.on('done', onDone)
    ctrl.on('received', onReceived)
    ctrl.on('function', onFunction)
    ctrl.on('error', onError)
    ctrl.on('resumed', onResumed)

    behaviorTracker.start()
    const userId = getValue('user-credentials')?._id || null
    ctrl.connect(userId)

    // Only trigger if no active session exists yet
    let triggerTimeout = null
    if (!ctrl.hasSession) {
      triggerTimeout = setTimeout(() => {
        const lastGreet = LocalUserPreferences.get('agent-data.last_greet')
        const greetedRecently = lastGreet && Date.now() - lastGreet < greetingIntervalMsRef.current

        if (!greetedRecently) LocalUserPreferences.set('agent-data.last_greet', Date.now())

        const snapshot = behaviorTracker.snapshot()
        const behavior = { ...snapshot, triggerKey }
        const frontendCapabilities = actionRegistry.getCapabilities()
        ctrl.trigger({
          behavior,
          frontendCapabilities,
          language: snapshot.language,
          trigger: triggerKey || 'idle',
          page: snapshot.pageUrl,
          suppressOpener: !!greetedRecently,
        })
      }, 600)
    }

    return () => {
      if (triggerTimeout) clearTimeout(triggerTimeout)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      clearWaitingTimeout()
      ctrl.off('chunk', onChunk)
      ctrl.off('done', onDone)
      ctrl.off('received', onReceived)
      ctrl.off('function', onFunction)
      ctrl.off('error', onError)
      ctrl.off('resumed', onResumed)
      behaviorTracker.stop()
    }
  }, [agentId, triggerKey, allowed])

  function handleSend(text) {
    const ctrl = controllerRef.current
    if (!ctrl) return
    chunkRef.current = ''
    setStreamedText('')
    bubbleVisibleRef.current = false; setBubbleVisible(false)
    setActionsVisible(false)
    setActions([])
    waitingRef.current = true; setWaiting(true)
    setInputDisabled(true)

    // Safety net: if no aa:received or aa:chunk within 15s, assume the message was lost
    if (waitingTimeoutRef.current) clearTimeout(waitingTimeoutRef.current)
    waitingTimeoutRef.current = setTimeout(() => {
      if (!waitingRef.current) return
      waitingRef.current = false; setWaiting(false)
      setInputDisabled(false)
    }, 15000)

    const behavior = behaviorTracker.snapshot()
    ctrl.sendMessage(text, behavior)
  }

  function handleOpenInput() {
    if (inputOpen) {
      setInputClosing(true)
      setTimeout(() => { setInputOpen(false); setInputClosing(false) }, 250)
      return
    }
    setInputOpen(true)
  }

  function handleDismissBubble() {
    bubbleVisibleRef.current = false
    setBubbleVisible(false)
    setActionsVisible(false)
  }

  function handleActionSelect(action) {
    setActionsVisible(false)
    setInputOpen(false)
    setInputClosing(false)
    // Navigate intent with a url — execute directly, no LLM round-trip
    if (action?.intent === 'navigate' && action?.url) {
      actionRegistry.execute('navigation.go', { path: action.url }).catch(err =>
        console.warn('[AldooAgent] navigation.go error:', err)
      )
      bubbleVisibleRef.current = false
      setBubbleVisible(false)
      chunkRef.current = ''
      setStreamedText('')
      return
    }
    handleSend(action?.label ?? action)
  }

  function handleProductBuy(pid) {
    actionRegistry.execute('cart.add', { pid }).catch(err =>
      console.warn('[AldooAgent] cart.add error:', err)
    )
    bubbleVisibleRef.current = false
    setBubbleVisible(false)
    chunkRef.current = ''
    setStreamedText('')
    setActionsVisible(false)
    setInputOpen(false)
    setInputClosing(false)
  }

  // Memoize expensive parse — only recompute when text changes
  const segments = useMemo(() => parseBubbleContent(streamedText), [streamedText])
  const textOnly = useMemo(
    () => segments.filter(s => s.type === 'text').map(s => s.value).join(''),
    [segments]
  )
  const products = useMemo(
    () => segments.filter(s => s.type === 'storeproduct').map(s => s.value),
    [segments]
  )

  const pos = POSITIONS[position] || POSITIONS['bottom-right']
  const isRight = position === 'bottom-right'

  const widgetStyle = {
    position: 'fixed',
    zIndex: 9999,
    fontFamily: FONT,
    display: 'flex',
    flexDirection: 'column',
    alignItems: isRight ? 'flex-end' : 'flex-start',
    width: 'min(440px, calc(100vw - 24px))',
    ...pos,
  }

  const bottomRowStyle = {
    display: 'flex',
    flexDirection: isRight ? 'row-reverse' : 'row',
    alignItems: 'flex-end',
    gap: 10,
    width: '100%',
  }

  const avatarColStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  }

  const avatarWrapStyle = {
    position: 'relative',
    width: 72,
    height: 72,
  }

  const avatarImgStyle = {
    width: 72,
    height: 72,
    borderRadius: 18,
    objectFit: 'cover',
    display: 'block',
    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
  }

  const pulseRingStyle = streaming ? {
    position: 'absolute',
    inset: -4,
    borderRadius: 22,
    border: '2px solid #10b981',
    animation: 'aa-pulse-ring 1.2s ease-out infinite',
  } : null

  if (allowed !== true) return null

  return (
    <>
      <style>{`
        @keyframes aa-pulse-ring {
          0%   { opacity: 0.8; transform: scale(1); }
          100% { opacity: 0; transform: scale(1.18); }
        }
        @keyframes aa-dot-bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40%            { transform: translateY(-6px); }
        }
        @keyframes aa-input-in {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes aa-input-out {
          from { opacity: 1; transform: translateY(0); }
          to   { opacity: 0; transform: translateY(10px); }
        }
      `}</style>
      <div style={widgetStyle}>
        {/* Action options */}
        <ActionList
          actions={actions}
          visible={actionsVisible}
          onSelect={handleActionSelect}
        />

        {/* Product card bubbles — side by side if space, stacked otherwise */}
        {products.length > 0 && (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            width: '100%',
            marginBottom: 4,
          }}>
            {products.map((product, i) => (
              <ProductBubble
                key={i}
                product={product}
                visible={bubbleVisible}
                onBuy={handleProductBuy}
              />
            ))}
          </div>
        )}

        {/* Text bubble — closest to avatar */}
        {textOnly.trim() && (
          <SpeechBubble
            text={textOnly}
            streaming={streaming}
            visible={bubbleVisible}
            onClick={handleDismissBubble}
            position={position}
          />
        )}

        {/* Bottom row: avatar + input */}
        <div style={bottomRowStyle}>
          <div style={avatarColStyle}>
            {/* Thinking dots centered above avatar */}
            {waiting && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
                <ThinkingDots />
              </div>
            )}
            <div
              style={{ ...avatarWrapStyle, cursor: 'pointer' }}
              onClick={handleOpenInput}
            >
              {avatarUrl
                ? <img src={url(avatarUrl)} alt={agentName} style={avatarImgStyle} />
                : <AvatarFallback name={agentName} />
              }
              {pulseRingStyle && <div style={pulseRingStyle} />}
            </div>
          </div>

          {inputOpen && (
            <div style={{ flex: 1, animation: `${inputClosing ? 'aa-input-out' : 'aa-input-in'} 0.25s ease forwards` }}>
              <InputBar
                onSend={handleSend}
                disabled={inputDisabled}
                agentName={agentName}
                placeholder={inputPlaceholder}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

function ThinkingDots() {
  const dotStyle = (delay) => ({
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#94a3b8',
    display: 'inline-block',
    margin: '0 2px',
    animation: `aa-dot-bounce 1.2s ease-in-out ${delay}ms infinite`,
  })
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <span style={dotStyle(0)} />
      <span style={dotStyle(160)} />
      <span style={dotStyle(320)} />
    </div>
  )
}

function AvatarFallback({ name }) {
  const initial = (name || 'A')[0].toUpperCase()
  return (
    <div style={{
      width: 72,
      height: 72,
      borderRadius: 18,
      background: 'linear-gradient(135deg, #6366f1, #10b981)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 26,
      color: '#fff',
      fontWeight: 700,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
    }}>
      {initial}
    </div>
  )
}
